import { Box, Button, Flex, Spinner, SvgIcon } from '@volue/wave-react';
import React from 'react';

import matchState from '@/common/utils/match';
import { Calculation } from '@/features/execute-calculation-set/components/Calculation/Calculation';
import { InlineNotification } from '@/features/execute-calculation-set/components/Calculation/InlineNotification';
import { toErrorWithDetailsFromCalculationSetExecutionError } from '@/features/execute-calculation-set/overmind/calculation/actions';
import { useActions, useAppState } from '@/overmind';

import { CenteredLoadingPage } from './CenteredLoadingPage';
import { CollapsibleError } from './CollapsibleError';
import { InlineError } from './InlineError';
import { InlineErrorWithRetryButton } from './InlineErrorWithRetryButton';

export const ExecuteCalculationSet = () => {
  const calculationTactic = useAppState().calculation.calculationTactic;
  const externallyTriggeredCalculationSetExecution =
    useAppState().calculation.externallyTriggeredCalculationSetExecution;
  const retryExecutingCalculationSet =
    useActions().calculation.retryExecutingCalculationSet;
  const featureGeometries = useAppState().arcgis.featureGeometries;
  const { showHomeWithRelatedGridElement } = useActions();

  return externallyTriggeredCalculationSetExecution.current ===
    'CANNOT_START' ? (
    <InlineError text={externallyTriggeredCalculationSetExecution.details} />
  ) : (
    matchState(calculationTactic, {
      LOADED: () =>
        matchState(featureGeometries, {
          LOADED: () =>
            calculationTactic.current === 'LOADED' &&
            calculationTactic.value.status !== 'Completed' ? (
              <InlineErrorWithRetryButton
                text={calculationTactic.value.details}
                handleRetryExecutingCalculationSet={
                  retryExecutingCalculationSet
                }
              />
            ) : (
              <Calculation />
            ),
          LOADING: () => (
            <Box
              className="h100 w100 dF jcC aiC beta fdC pHxl max-h-4xl"
              css={{ color: '$colorAccent70' }}
            >
              <InlineNotification
                type="SUCCESS"
                className="mAl"
                content={
                  <Flex
                    flow={'column'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    css={{ fontSize: '$delta' }}
                  >
                    <Box css={{ fontWeight: '$semibold' }}>
                      Execution of calculations set completed successfully!
                    </Box>
                    <Flex
                      justifyContent={'center'}
                      alignItems={'center'}
                      gap={'spacingS'}
                    >
                      <Box>Preparing calculation set results...</Box>
                      <Flex justifyContent={'center'} alignItems="center">
                        <Spinner size="small" inline />
                      </Flex>
                    </Flex>
                  </Flex>
                }
              />
            </Box>
          ),
          ERROR: loadingError => (
            <InlineErrorWithRetryButton
              text={loadingError.error}
              handleRetryExecutingCalculationSet={retryExecutingCalculationSet}
            />
          ),
          NOT_LOADED: () => (
            <CenteredLoadingPage text="Loading feature titles" />
          )
        }),
      LOADING: () => (
        <CenteredLoadingPage text="Executing calculation set..." />
      ),
      ERROR: errorCalculationSetExecution => {
        const errorWithDetails =
          toErrorWithDetailsFromCalculationSetExecutionError(
            errorCalculationSetExecution
          );
        return (
          <InlineErrorWithRetryButton
            text={<CollapsibleError error={errorWithDetails} />}
            handleRetryExecutingCalculationSet={retryExecutingCalculationSet}
          >
            <Button
              className="w100 txtC"
              leftIcon={<SvgIcon iconName="close" />}
              onClick={showHomeWithRelatedGridElement}
            >
              {'Close'}
            </Button>
          </InlineErrorWithRetryButton>
        );
      },
      NOT_LOADED: () => <CenteredLoadingPage text="Executing calculation set" />
    })
  );
};
